import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, subDays, addDays } from "date-fns";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PatientService from "../../services/PatientService";

interface Deadline {
  local_time: string;
  status: string;
  measurements: any[];
}

export interface EngagementDetails {
  [measurementType: string]: {
    deadlines: Deadline[];
    STATUS?: string;
  };
}

interface EngagementDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  patient_id: string;
  date: string; // The selected day (e.g. "yyyy-MM-dd")
  token: string;
}

export const EngagementDetailsDialog: React.FC<EngagementDetailsDialogProps> = ({
  open,
  onClose,
  patient_id,
  date,
  token,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [engagementData, setEngagementData] = useState<EngagementDetails | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(date));

  // Helper function to get a proper display name for measurement types
  const getMeasurementTypeDisplayName = (measurementType: string): string => {
    const displayNameMap: { [key: string]: string } = {
      "oxygen_saturation": t("SpO2"),
      "perfusion_index": t("PI"), 
      "fev1": t("FEV1"),
      "pef": t("PEF"),
      "heart_rate": t("HR"),
      "systolic": t("Systolic") + " BP",
      "diastolic": t("Diastolic") + " BP",
      "mean_arterial_pressure": t("MAP"),
      "SpO2": t("SpO2"),
      "PeakFlow": t("PeakFlow"),
      "BloodPressure": t("BloodPressure"),
      "HeartRate": t("HeartRate")
    };
    
    // Return mapped name if available, otherwise try translation, otherwise return as-is
    return displayNameMap[measurementType] || t(measurementType) || measurementType;
  };

  // Update selectedDate when "date" prop changes.
  useEffect(() => {
    setSelectedDate(new Date(date));
  }, [date]);

  // Fetch engagement details whenever the dialog opens or selectedDate changes.
  useEffect(() => {
    if (open) {
      setLoading(true);
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      PatientService.fetchEngagementDetails(token, patient_id, formattedDate)
        .then((data: any) => {
          console.log("Raw engagement details data:", data);
          
          let engagementObj: EngagementDetails;
          if (Array.isArray(data)) {
            engagementObj = data.reduce((acc: EngagementDetails, category: any) => {
              // Filter out invalid measurement types like "AM", "PM", etc.
              const invalidMeasurementTypes = ["AM", "PM", "am", "pm"];
              if (category.measurement_type && !invalidMeasurementTypes.includes(category.measurement_type)) {
                acc[category.measurement_type] = {
                  deadlines: category.deadlines,
                  STATUS: category.STATUS,
                };
              } else {
                console.warn("Invalid or missing measurement_type detected:", category.measurement_type, "Full category:", category);
              }
              return acc;
            }, {} as EngagementDetails);
          } else if (data && typeof data === "object") {
            engagementObj = data;
          } else {
            engagementObj = {} as EngagementDetails;
          }
          setEngagementData(engagementObj);
        })
        .catch((error) => {
          console.error("Error fetching engagement details:", error);
          setEngagementData(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setEngagementData(null);
    }
  }, [open, patient_id, selectedDate, token]);

  // Helper function to extract a timestamp from a measurement.
  const extractTimestamp = (measurement: any): string | null => {
    if (measurement.timestamp) return measurement.timestamp;
    if (measurement.oxygen_saturation?.timestamp)
      return measurement.oxygen_saturation.timestamp;
    if (measurement.perfusion_index?.timestamp)
      return measurement.perfusion_index.timestamp;
    for (const key in measurement) {
      if (measurement[key]?.timestamp) {
        return measurement[key].timestamp;
      }
    }
    return null;
  };

  // Helper function to group measurements by timestamp and format them
  const groupMeasurementsByTimestamp = (measurements: any[]): { timestamp: string; formattedMeasurements: string }[] => {
    //console.log("DEBUG: groupMeasurementsByTimestamp called with:", measurements);
    
    // Group related measurements
    const measurementGroups: { [key: string]: string[] } = {
      SpO2: ["oxygen_saturation", "perfusion_index"],
      PeakFlow: ["fev1", "pef"],
      BloodPressure: ["systolic", "diastolic", "mean_arterial_pressure"],
      HeartRate: ["heart_rate"],
    };

    // Mapping of field names to abbreviated labels
    const abbreviatedLabels: { [key: string]: string } = {
      oxygen_saturation: t("SpO2"),
      perfusion_index: t("PI"),
      fev1: t("FEV1"),
      pef: t("PEF"),
      heart_rate: t("HR"),
      systolic: t("Systolic"),
      diastolic: t("Diastolic"),
      mean_arterial_pressure: t("MAP"),
    };

    // Helper function to format values based on unit
    const formatValue = (value: any, unit: string): string => {
      if (unit === '%' && typeof value === 'number') {
        return value.toFixed(2);
      }
      return value.toString();
    };

    // Group measurements by timestamp
    const timestampGroups: { [timestamp: string]: any[] } = {};
    measurements.forEach(measurement => {
      const timestamp = extractTimestamp(measurement);
      if (timestamp) {
        if (!timestampGroups[timestamp]) {
          timestampGroups[timestamp] = [];
        }
        timestampGroups[timestamp].push(measurement);
      }
    });

    // Process each timestamp group
    const result: { timestamp: string; formattedMeasurements: string }[] = [];
    Object.entries(timestampGroups).forEach(([timestamp, measurementsAtTime]) => {
      const processed = new Set();
      const formattedLines: string[] = [];
      
      // Collect all measurements at this timestamp
      const availableMeasurements: { [key: string]: { value: any; unit: string } } = {};
      measurementsAtTime.forEach(measurement => {
        Object.entries(measurement).forEach(([name, data]) => {
          if (name !== "timestamp" && typeof data === "object" && data !== null && "value" in data && "unit" in data) {
            availableMeasurements[name] = data as { value: any; unit: string };
          }
        });
      });

      // Process measurement groups
      Object.entries(measurementGroups).forEach(([groupName, fields]) => {
        const groupValues: string[] = [];
        fields.forEach((field) => {
          if (availableMeasurements[field] && !processed.has(field)) {
            const data = availableMeasurements[field];
            const label = abbreviatedLabels[field] || field;
            const formattedValue = formatValue(data.value, data.unit);
            groupValues.push(`${label}: ${formattedValue} ${data.unit}`);
            processed.add(field);
          }
        });
        if (groupValues.length > 0) {
          formattedLines.push(groupValues.join(", "));
        }
      });

      // Process remaining fields
      Object.entries(availableMeasurements).forEach(([name, data]) => {
        if (!processed.has(name)) {
          const label = abbreviatedLabels[name] || name;
          const formattedValue = formatValue(data.value, data.unit);
          formattedLines.push(`${label}: ${formattedValue} ${data.unit}`);
        }
      });

      result.push({
        timestamp,
        formattedMeasurements: formattedLines.join(", ")
      });
    });

    //console.log("DEBUG: groupMeasurementsByTimestamp returning:", result);
    return result;
  };

  // Get today's date with time stripped.
  const today = new Date(new Date().toDateString());
  const isToday = selectedDate.getTime() >= today.getTime();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md"  
            sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
            PaperProps={{
            sx: { mt: 12 } // Adjust the margin-top as needed (e.g., 2, 4, etc.)
            }}
        >
        <DialogTitle>
        <Grid container alignItems="center">
            {/* Left: Patient ID */}
            <Grid item xs={6}>
            <Typography variant="h6">
                {t("Patient ID")}: {patient_id}
            </Typography>
            </Grid>
            {/* Right: Day navigator */}
            <Grid item xs={6}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
                <IconButton onClick={() => setSelectedDate(prev => subDays(prev, 1))}>
                <ArrowBackIosIcon fontSize="small" />
                </IconButton>
                {/* Fixed width container for the date */}
                <Box sx={{ width: "220px", textAlign: "center" }}>
                <Typography variant="h6">
                    {format(selectedDate, "MMMM dd, yyyy")}
                </Typography>
                </Box>
                <IconButton
                onClick={() => setSelectedDate(prev => addDays(prev, 1))}
                disabled={isToday}
                >
                <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
            </Box>
            </Grid>
        </Grid>
        </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={100}>
            <CircularProgress />
          </Box>
        ) : engagementData ? (
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Measurement Type")}</TableCell>
                  <TableCell>{t("Deadline")}</TableCell>
                  <TableCell>{t("Status")}</TableCell>
                  <TableCell>{t("Time")}</TableCell>
                  <TableCell>{t("Measurements")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(engagementData).map(([measurementType, category], catIndex) => {
                  // Calculate total rows for this measurement type
                  const totalRowsForMeasurement = category.deadlines.reduce((total, deadline) => {
                    const groupedMeasurements = groupMeasurementsByTimestamp(deadline.measurements);
                    return total + Math.max(1, groupedMeasurements.length); // At least 1 row even if no measurements
                  }, 0);

                  let currentRowIndex = 0;

                  return (
                    <React.Fragment key={`cat-${catIndex}`}>
                      {category.deadlines.map((deadline, deadIndex) => {
                        const groupedMeasurements = groupMeasurementsByTimestamp(deadline.measurements);
                        const measurementRows = groupedMeasurements.length > 0 ? groupedMeasurements : [{ timestamp: '', formattedMeasurements: 'No measurements' }];
                        
                        return measurementRows.map((group, groupIndex) => {
                          const isFirstRowOfTable = currentRowIndex === 0;
                          const isFirstRowOfDeadline = groupIndex === 0;
                          currentRowIndex++;

                          return (
                            <TableRow key={`cat-${catIndex}-dead-${deadIndex}-group-${groupIndex}`}>
                              {isFirstRowOfTable && (
                                <TableCell rowSpan={totalRowsForMeasurement}>
                                  {getMeasurementTypeDisplayName(measurementType)}
                                </TableCell>
                              )}
                              {isFirstRowOfDeadline && (
                                <>
                                  <TableCell rowSpan={measurementRows.length}>{deadline.local_time}</TableCell>
                                  <TableCell rowSpan={measurementRows.length} sx={{ color: deadline.status === "MET" ? "green" : "red" }}>
                                    {deadline.status}
                                  </TableCell>
                                </>
                              )}
                              <TableCell>
                                {group.timestamp ? new Date(group.timestamp).toLocaleTimeString() : '--'}
                              </TableCell>
                              <TableCell>
                                {group.formattedMeasurements}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>{t("No Engagement Details Found")}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EngagementDetailsDialog;
